
import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface SubscriptionFiltersProps {
  onFilterChange: (filters: SubscriptionFilters) => void;
  className?: string;
  cities?: string[]; // Optional array of cities for the city filter
}

export interface SubscriptionFilters {
  search: string;
  status: string | null;
  housingType: string | null;
  city: string | null;
}

const initialFilters: SubscriptionFilters = {
  search: '',
  status: null,
  housingType: null,
  city: null,
};

const SubscriptionFilters: React.FC<SubscriptionFiltersProps> = ({
  onFilterChange,
  className,
  cities = ['Stockholm', 'Gothenburg', 'Malmö', 'Uppsala', 'Linköping']
}) => {
  const [filters, setFilters] = useState<SubscriptionFilters>(initialFilters);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFilterChange = useCallback((key: keyof SubscriptionFilters, value: string | null) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  }, [filters, onFilterChange]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFilterChange('search', e.target.value);
  }, [handleFilterChange]);

  const handleReset = useCallback(() => {
    setFilters(initialFilters);
    onFilterChange(initialFilters);
  }, [onFilterChange]);

  const hasActiveFilters = !!filters.status || !!filters.housingType || !!filters.city || !!filters.search;

  return (
    <div className={cn("glass-card p-4", className)}>
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by subscription number, customer, address, Alarm.com ID, phone, or email..."
            value={filters.search}
            onChange={handleSearchChange}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              "transition-colors",
              isExpanded && "bg-secondary",
              hasActiveFilters && "border-primary text-primary"
            )}
          >
            <Filter className="h-4 w-4" />
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="icon"
              onClick={handleReset}
              className="text-muted-foreground hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 animate-fade-in">
          <div>
            <label className="text-sm font-medium mb-2 block">Status</label>
            <Select
              value={filters.status || 'all-statuses'}
              onValueChange={(value) => handleFilterChange('status', value === 'all-statuses' ? null : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-statuses">All statuses</SelectItem>
                <SelectItem value="registered">Registered</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="terminated">Terminated</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Housing Type</label>
            <Select
              value={filters.housingType || 'all-types'}
              onValueChange={(value) => handleFilterChange('housingType', value === 'all-types' ? null : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-types">All types</SelectItem>
                <SelectItem value="house">House</SelectItem>
                <SelectItem value="apartment">Apartment</SelectItem>
                <SelectItem value="office">Office</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">City</label>
            <Select
              value={filters.city || 'all-cities'}
              onValueChange={(value) => handleFilterChange('city', value === 'all-cities' ? null : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-cities">All cities</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city} value={city.toLowerCase()}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionFilters;
